import { Component, JSX, Show, createEffect, onCleanup, onMount } from 'solid-js';
import { debug } from '../lib/debug';

interface VirtualizedNoteSlotProps {
  eventId: string;
  isVirtualized: boolean;
  virtualHeight?: number;
  onVirtualize: (height: number) => void;
  onUnvirtualize: () => void;
  canVirtualize: boolean;
  children: JSX.Element;
}

const VIRTUALIZATION_MARGIN_PX = typeof window !== 'undefined'
  ? Math.max(1800, window.innerHeight * 2)
  : 3600; // SSR fallback -- 2-screen buffer for instant scroll-back rehydration

export const VirtualizedNoteSlot: Component<VirtualizedNoteSlotProps> = (props) => {
  let containerRef: HTMLDivElement | undefined;
  let observer: IntersectionObserver | null = null;
  let virtualizeTimer: number | null = null;
  let currentVirtualized = props.isVirtualized;
  let frameScheduled = false;

  const isWithinViewport = () => {
    if (!containerRef) return false;
    const rect = containerRef.getBoundingClientRect();
    const margin = VIRTUALIZATION_MARGIN_PX;
    return rect.bottom >= -margin && rect.top <= window.innerHeight + margin;
  };

  const forceRehydrateIfVisible = () => {
    if (!currentVirtualized || !props.canVirtualize) return;
    if (!containerRef) return;
    if (isWithinViewport()) {
      cancelPendingVirtualize();
      if (currentVirtualized) {
        debug('[VirtualizedNoteSlot] forcing rehydrate (visibility check)', props.eventId);
        props.onUnvirtualize();
        currentVirtualized = false;
      }
    }
  };

  const cancelPendingVirtualize = () => {
    if (virtualizeTimer !== null) {
      clearTimeout(virtualizeTimer);
      virtualizeTimer = null;
    }
  };

  const scheduleVirtualize = (height: number) => {
    if (virtualizeTimer !== null) return;
    virtualizeTimer = window.setTimeout(() => {
      virtualizeTimer = null;
      if (!props.canVirtualize || currentVirtualized) return;
      debug('[VirtualizedNoteSlot] virtualizing', props.eventId, height);
      props.onVirtualize(Math.max(1, height));
      currentVirtualized = true;
    }, 120);
  };

  createEffect(() => {
    const previousVirtualized = currentVirtualized;
    const nextVirtualized = props.isVirtualized;
    currentVirtualized = nextVirtualized;

    if (!props.canVirtualize && nextVirtualized) {
      debug('[VirtualizedNoteSlot] forcing hydration for', props.eventId);
      props.onUnvirtualize();
      currentVirtualized = false;
    }
    if (!props.canVirtualize) {
      cancelPendingVirtualize();
    }

    if (nextVirtualized && !previousVirtualized) {
      // Parent reconstructed virtualization state; ensure we hydrate immediately if needed.
      queueMicrotask(() => forceRehydrateIfVisible());
    }
  });

  onMount(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting) {
          cancelPendingVirtualize();
          if (currentVirtualized) {
            debug('[VirtualizedNoteSlot] rehydrating', props.eventId);
            props.onUnvirtualize();
            currentVirtualized = false;
          }
        } else {
          if (!props.canVirtualize) {
            cancelPendingVirtualize();
            return;
          }
          if (!currentVirtualized) {
            const measuredHeight = entry.boundingClientRect.height || containerRef?.offsetHeight || props.virtualHeight || 0;
            scheduleVirtualize(measuredHeight);
          }
        }
      },
      {
        root: null,
        rootMargin: `${VIRTUALIZATION_MARGIN_PX}px 0px`,
        threshold: 0,
      }
    );

    if (containerRef) {
      observer.observe(containerRef);
    }

    const scheduleCheck = () => {
      if (frameScheduled) return;
      frameScheduled = true;
      requestAnimationFrame(() => {
        frameScheduled = false;
        forceRehydrateIfVisible();
      });
    };

    window.addEventListener('scroll', scheduleCheck, { passive: true });
    window.addEventListener('resize', scheduleCheck);
    onCleanup(() => {
      window.removeEventListener('scroll', scheduleCheck);
      window.removeEventListener('resize', scheduleCheck);
    });
  });

  onCleanup(() => {
    observer?.disconnect();
    observer = null;
    cancelPendingVirtualize();
  });

  return (
    <div
      ref={containerRef}
      data-note-id={props.eventId}
    >
      <Show
        when={!props.isVirtualized || !props.canVirtualize}
        fallback={
          <div
            class="virtual-note-placeholder mb-10 border border-dashed border-[var(--border-color)] rounded-md bg-[var(--bg-secondary)]/50"
            style={{ height: `${props.virtualHeight ?? 0}px`, 'min-height': '80px' }}
          />
        }
      >
        {props.children}
      </Show>
    </div>
  );
};
